import { isEmpty, isNil } from 'lodash';

export interface IndexResult<T> {
    readonly matches: Map<string, number>;
    readonly suggestions: IndexSuggestion<T>[];
}

export interface IndexSuggestion<T> {
    readonly token: string;
    readonly hits: number;
    readonly items: IndexedItem<T>[];
}

interface IndexedItem<T> {
    key: string;
    value: T;
}

export interface IndexEntry<T> {
    readonly token: string;
    readonly item: IndexedItem<T>;
}

interface IndexItem<T> {
    readonly token: string;
    readonly lowerCaseValue: string;
    readonly items: IndexedItem<T>[];
}

export class SearchIndex<T> {
    private readonly index: IndexItem<T>[];

    constructor(tokens: IndexEntry<T>[]) {
        const tempIndex = new Map<string, IndexedItem<T>[]>();
        tokens.forEach((token) => {
            const value = token.token;
            if (!tempIndex.has(value)) {
                tempIndex.set(value, []);
            }
            tempIndex.get(value)?.push(token.item);
        });

        this.index = Array.from(tempIndex.entries()).map(([value, keys]) => ({
            token: value,
            items: keys,
            lowerCaseValue: value.toLowerCase()
        }));
    }

    search(query: string | undefined): IndexResult<T> {
        if (isNil(query) || isEmpty(query)) {
            return { matches: new Map(), suggestions: [] };
        }

        const queryParts = query
            .toLowerCase()
            .split(' ')
            .filter((part) => part.length > 0);

        const matches = new Map<string, number>();

        const itemHits = this.index
            .map((item) => {
                const count = queryParts.filter((part) => item.lowerCaseValue.includes(part)).length;
                return { item, count };
            })
            .filter((hit) => hit.count > 0);

        itemHits.forEach(({ item, count }) => {
            item.items.forEach((key) => matches.set(key.key, (matches.get(key.key) ?? 0) + count));
        });

        return {
            matches,
            suggestions:
                query.length > 1
                    ? itemHits.map(({ item, count }) => ({
                          token: item.token,
                          hits: count,
                          items: item.items
                      }))
                    : []
        };
    }
}
